import { useState } from 'react';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useHome, type HomeAssistant } from '@entities/home';
import { useOperatorSignal } from '@entities/operator';
import { useUserStore } from '@entities/user';
import { useLogoutMutation } from '@features/auth';
import {
  IconCard,
  IconCode,
  IconCollapse,
  IconExpand,
  IconHelp,
  IconHome,
  IconLogout,
  IconPlus,
  IconSection,
  IconUser,
  LetterAvatar,
  shortName,
} from '@shared/ui/v2';

/** Разделы ассистента — порядок и подписи из утверждённого экрана 3. */
export const ASSISTANT_SECTIONS = [
  { id: 'settings', label: 'Настройка' },
  { id: 'test-chat', label: 'Тест-чат' },
  { id: 'knowledge', label: 'База знаний' },
  { id: 'channels', label: 'Каналы' },
  { id: 'dialogs', label: 'Диалоги' },
  { id: 'broadcasts', label: 'Рассылки' },
] as const;

const STATUS: Record<HomeAssistant['status'], { color: string; title: string }> = {
  working: { color: '#16A34A', title: 'Работает' },
  stopped: { color: '#9CA3AF', title: 'Остановлен' },
  no_balance: { color: '#DC2626', title: 'Нет баланса' },
};

const STORAGE_KEY = 'richat.nav.collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(v: boolean) {
  try {
    window.localStorage.setItem(STORAGE_KEY, v ? '1' : '0');
  } catch {
    /* хранилище недоступно — просто не запоминаем */
  }
}

/**
 * Левое меню кабинета v2 (экран 0/1): закреплена только строка логотипа,
 * остальное прокручивается; сворачивается в полосу иконок на любом экране.
 */
export function AppNav() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const location = useLocation();
  const match = useMatch('/assistants/:id/*');
  const openId = match?.params.id;
  const section = match?.params['*']?.split('/')[0] || 'settings';
  const user = useUserStore((s) => s.user);
  const { data: home } = useHome();
  const operatorWaiting = useOperatorSignal();
  const navigate = useNavigate();
  const logout = useLogoutMutation();
  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
    } finally {
      navigate('/login');
    }
  };

  // Список — из /cabinet/home (там же статус); пока грузится — из профиля.
  const assistants: Array<
    Pick<HomeAssistant, 'id' | 'name'> & { status?: HomeAssistant['status'] }
  > = home?.assistants ?? user?.assistants ?? [];

  const toggle = () => {
    setCollapsed((v) => {
      writeCollapsed(!v);
      return !v;
    });
  };

  const top: Array<{
    to: string;
    label: string;
    icon: JSX.Element;
    active: boolean;
    badge?: number;
  }> = [
    { to: '/', label: 'Главная', icon: <IconHome />, active: location.pathname === '/' },
    {
      to: '/operators',
      label: 'Операторы',
      icon: <IconUser />,
      active: location.pathname.startsWith('/operators'),
      badge: operatorWaiting,
    },
    {
      to: '/billing',
      label: 'Баланс и расход',
      icon: <IconCard />,
      active: location.pathname.startsWith('/billing'),
    },
    {
      to: '/api-access',
      label: 'API',
      icon: <IconCode />,
      active: location.pathname.startsWith('/api-access'),
    },
  ];
  const helpActive = location.pathname.startsWith('/getting-started');
  const settingsActive = location.pathname.startsWith('/settings');
  const email = user?.email ?? 'Аккаунт';

  if (collapsed) {
    return (
      <nav className="v2-nav is-collapsed" aria-label="Основное меню">
        <button
          type="button"
          className="v2-nav-icon"
          onClick={toggle}
          aria-label="Развернуть меню"
          title="Развернуть меню"
        >
          <IconExpand />
        </button>
        <div className="v2-nav-body">
          <Link
            to="/assistants/new"
            className="v2-nav-icon"
            style={{ marginBottom: 4 }}
            aria-label="Новый ассистент"
            title="Новый ассистент"
          >
            <IconPlus size={20} strokeWidth={1.8} />
          </Link>
          {top.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              className={t.active ? 'v2-nav-icon is-active' : 'v2-nav-icon'}
              aria-label={t.label}
              title={t.label}
              aria-current={t.active ? 'page' : undefined}
            >
              {t.icon}
              {!!t.badge && <span className="v2-dot-badge" style={{ background: '#DC2626' }} />}
            </Link>
          ))}
          <div className="v2-nav-sep" />
          {assistants.map((a) => (
            <div key={a.id} style={{ display: 'contents' }}>
              <Link
                to={`/assistants/${a.id}/settings`}
                className="v2-nav-icon"
                aria-label={a.name}
                title={a.name}
              >
                <LetterAvatar name={a.name} colorKey={a.id} size={28} />
                {a.status && (
                  <span className="v2-dot-badge" style={{ background: STATUS[a.status].color }} />
                )}
              </Link>
              {a.id === openId &&
                ASSISTANT_SECTIONS.map((s) => (
                  <Link
                    key={s.id}
                    to={`/assistants/${a.id}/${s.id}`}
                    className={
                      s.id === section ? 'v2-nav-icon is-sub is-active' : 'v2-nav-icon is-sub'
                    }
                    aria-label={s.label}
                    title={s.label}
                    aria-current={s.id === section ? 'page' : undefined}
                  >
                    <IconSection id={s.id} size={17} />
                  </Link>
                ))}
            </div>
          ))}
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Link
              to="/getting-started"
              className={helpActive ? 'v2-nav-icon is-active' : 'v2-nav-icon'}
              aria-label="Помощь и обучение"
              title="Помощь и обучение"
            >
              <IconHelp />
            </Link>
            <Link
              to="/settings"
              className={settingsActive ? 'v2-nav-icon is-active' : 'v2-nav-icon'}
              aria-label="Настройки аккаунта"
              title={`Настройки аккаунта · ${email}`}
            >
              <IconUser />
            </Link>
            <button
              type="button"
              className="v2-nav-icon"
              onClick={handleLogout}
              aria-label="Выйти"
              title="Выйти"
            >
              <IconLogout />
            </button>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="v2-nav" aria-label="Основное меню">
      <div className="v2-nav-head">
        <Link to="/" className="v2-logo" aria-label="RiChat — на главную">
          RICHAT
        </Link>
        <button
          type="button"
          className="v2-icon-btn"
          onClick={toggle}
          aria-label="Свернуть меню"
          title="Свернуть меню"
        >
          <IconCollapse />
        </button>
      </div>
      <div className="v2-nav-body">
        <Link to="/assistants/new" className="v2-new-btn">
          <IconPlus size={16} />
          Новый ассистент
        </Link>
        <div className="v2-nav-group">
          {top.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              className={t.active ? 'v2-nav-item is-active' : 'v2-nav-item'}
              aria-current={t.active ? 'page' : undefined}
            >
              {t.icon}
              {t.label}
              {!!t.badge && (
                <span className="v2-nav-count" aria-label={`Ждут ответа: ${t.badge}`}>
                  {t.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
        <div className="v2-nav-group">
          <div className="v2-nav-caption">АССИСТЕНТЫ</div>
          {assistants.length === 0 && (
            <div className="v2-muted v2-small" style={{ padding: '4px 12px' }}>
              Пока ни одного
            </div>
          )}
          {assistants.map((a) => {
            const isOpen = a.id === openId;
            return (
              <div key={a.id}>
                <Link
                  to={`/assistants/${a.id}/settings`}
                  className={isOpen ? 'v2-nav-assistant is-open' : 'v2-nav-assistant'}
                  title={a.name}
                >
                  <LetterAvatar name={a.name} colorKey={a.id} size={26} />
                  <span className="v2-name">{shortName(a.name)}</span>
                  {a.status && (
                    <span
                      className="v2-dot"
                      title={STATUS[a.status].title}
                      style={{ background: STATUS[a.status].color }}
                    />
                  )}
                </Link>
                {isOpen && (
                  <div className="v2-nav-sub">
                    {ASSISTANT_SECTIONS.map((s) => (
                      <Link
                        key={s.id}
                        to={`/assistants/${a.id}/${s.id}`}
                        className={s.id === section ? 'v2-nav-subitem is-active' : 'v2-nav-subitem'}
                        aria-current={s.id === section ? 'page' : undefined}
                      >
                        <IconSection id={s.id} size={16} />
                        {s.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="v2-nav-foot">
          <Link
            to="/getting-started"
            className={helpActive ? 'v2-nav-item is-active' : 'v2-nav-item'}
          >
            <IconHelp />
            <span>Помощь и обучение</span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link
              to="/settings"
              className={settingsActive ? 'v2-nav-item is-active' : 'v2-nav-item'}
              title="Настройки аккаунта"
              style={{ flex: 1, minWidth: 0 }}
            >
              <IconUser />
              <span>{email}</span>
            </Link>
            <button
              type="button"
              className="v2-icon-btn"
              onClick={handleLogout}
              aria-label="Выйти"
              title="Выйти"
            >
              <IconLogout size={17} />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
